import {FieldValues} from 'react-hook-form';
import * as yup from 'yup';
import {TField} from '~/components';

export const isDevBuild =
  process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;

//Seperate from node-env, as that is typically expected to be 'development' or 'production',
//and semantically means dev build or prod build
export const TFR_ENV = import.meta.env.PUBLIC_TFR_ENV ?? 'local';

export const TOKEN_NAME = `tfr-fe${
  TFR_ENV !== 'production' ? `-${TFR_ENV}` : ''
}:token`;

export const SHOPIFY_CUSTOMER_TOKEN_NAME = `tfr-fe${
  TFR_ENV !== 'production' ? `-${TFR_ENV}` : ''
}:token-shopify-customer`;

export const DISPLAY_TIMEZONE = 'Asia/Singapore';

export const PUBLIC_BACKEND_BASE_URL = import.meta.env
  .PUBLIC_BACKEND_BASE_URL as string;

export const PUBLIC_SHOPIFY_STORE_DOMAIN = import.meta.env
  .PUBLIC_SHOPIFY_STORE_DOMAIN as string;

export const PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN = import.meta.env
  .PUBLIC_SHOPIFY_STOREFRONT_ACCESS_TOKEN as string;

export const PUBLIC_SHOPIFY_DELEGATE_ACCESS_TOKEN = import.meta.env
  .PUBLIC_SHOPIFY_DELEGATE_ACCESS_TOKEN as string;

export const PUBLIC_JUDGEME_SHOP_DOMAIN = import.meta.env
  .PUBLIC_JUDGEME_SHOP_DOMAIN as string;

export const PUBLIC_JUDGEME_PUBLIC_TOKEN = import.meta.env
  .PUBLIC_JUDGEME_PUBLIC_TOKEN as string;

export const PUBLIC_JUDGEME_CDN_HOST = import.meta.env
  .PUBLIC_JUDGEME_CDN_HOST as string;

export const PUBLIC_TYPEFORM_ID = import.meta.env.PUBLIC_TYPEFORM_ID as string;

export const PUBLIC_FACEBOOK_APP_ID = import.meta.env
  .PUBLIC_FACEBOOK_APP_ID as string;

export const PUBLIC_GOOGLE_CLIENT_ID = import.meta.env
  .PUBLIC_GOOGLE_CLIENT_ID as string;

export const PUBLIC_KLAVIYO_COMPANY_ID = import.meta.env
  .PUBLIC_KLAVIYO_COMPANY_ID as string;

//Shouldn't be public!
export const PUBLIC_KLAVIYO_API_KEY = import.meta.env
  .PUBLIC_KLAVIYO_PUBLIC_API_KEY as string;

export const CONSULTANT_TYPEFORM_URL =
  'https://hello439803.typeform.com/tfrconsultant';

export const PUBLIC_PRODUCT_LINK_PREFIX = import.meta.env
  .PUBLIC_PRODUCT_LINK_PREFIX as string;

export const STRAPI_BASE_URL = import.meta.env.PUBLIC_STRAPI_BASE_URL as string;
export const STRAPI_ACCESS_TOKEN = import.meta.env
  .PUBLIC_STRAPI_ACCESS_TOKEN as string;

export const SHOPWAIVE_PLATFORM = import.meta.env
  .PUBLIC_SHOPWAIVE_PLATFORM as string;
export const SHOPWAIVE_BASE_URL = import.meta.env
  .PUBLIC_SHOPWAIVE_BASE_URL as string;
export const SHOPWAIVE_ACCESS_TOKEN = import.meta.env
  .PUBLIC_SHOPWAIVE_ACCESS_TOKEN as string;
export const PUBLIC_HYGRAPH_URL = import.meta.env.PUBLIC_HYGRAPH_URL;

export const whatsappUrl =
  'https://api.whatsapp.com/send/?phone=6597319082&text&type=phone_number&app_absent=0';

export const getWhatsappUrlWithText = (text: string) =>
  `https://api.whatsapp.com/send/?phone=6597319082&text=${text}&type=phone_number&app_absent=0`;

interface Social {
  allowed_hosts: string[];
}

interface IPreferencesField extends TField<FieldValues> {
  multiple?: boolean;
}

export interface Preferences {
  id: string;
  title: string;
  validationSchema: yup.AnyObjectSchema;
  fields: IPreferencesField[];
}

interface Constants {
  social: Social;
  preferences: Preferences[];
}

const acceptanceSchema = yup
  .boolean()
  .oneOf([true], 'Please accept to proceed')
  .required();

const stringArraySchema = yup
  .array()
  .of(yup.string())
  .min(1, 'Choose at least one item.');

const Constants: Constants = {
  social: {
    allowed_hosts: [
      'http://localhost:3000',
      'https://dev.thefirstrefresh.workers.dev',
      'https://thefirstrefresh.store',
      'https://www.thefirstrefresh.store',
      'https://tfr-riez.thefirstrefresh.workers.dev',
      'https://tfr-leonard.thefirstrefresh.workers.dev',
    ],
  },
  preferences: [
    {
      id: 'general',
      title: 'General',
      validationSchema: yup.object().shape({
        ideal_session: yup.string().required(),
        health_conditions: yup.string().required(),
        currently: yup.string().required(),
        allergies: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'ideal_session',
          title: 'Ideal Session',
          type: 'choices',
          choices: [
            'Zen: complete silence - so you can take the time to reset',
            'Chill: moderate conversation and some chill',
            'Fun: an interactive chat session with our friendly beauty consultants',
          ],
        },
        {
          ref: 'health_conditions',
          title: 'Health Conditions',
          description:
            'Please let us know if there are any health conditions or concerns that we should be aware of in providing your beauty services. Examples of contraindications include skin or eye sensitivity, eczema / psoriasis, skin allergies, cancer, alopecia, surgery at the treatment area etc.',
          type: 'text',
        },
        {
          ref: 'currently',
          title: 'Are you currently any of the following?',
          type: 'choices',
          choices: ['Pregnant', 'Breastfeeding', 'NA'],
        },
        {
          ref: 'allergies',
          title: 'Allergies',
          description:
            'Please let us know if you have any allergies that we should be aware of. Examples of allergies include fragrance/essential oil sensitivity, eye sensitivity to lash glues etc.',
          type: 'text',
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'nails',
      title: 'Nails',
      validationSchema: yup.object().shape({
        polish_type: yup.string().required(),
        experienced: yup.string().required(),
        nail_type: yup.string().required(),
        preferred_nail_shape: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'polish_type',
          title: 'Polish Type',
          type: 'choices',
          choices: ['Gel', 'Halal (Non-Toxic)', 'Classic'],
        },
        {
          ref: 'experienced',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          type: 'choices',
          choices: [
            'Unusual skin reactions to products and creams used in the nail session',
            'Small cuts during cuticle cutting due to dry skin',
            'Nails chipping quickly post-service',
            'I have not experienced any of the above',
          ],
        },
        {
          ref: 'nail_type',
          title: 'Nail Type',
          type: 'choices',
          choices: ['Strong and hardy nails', 'Thin and brittle nails'],
        },
        {
          ref: 'preferred_nail_shape',
          title: 'Preferred Nail Shape',
          type: 'choices',
          choices: [
            'Square (square nails chip much easier because of sharp edges!)',
            'Square with rounded edges',
            'Round',
          ],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'lash_lab',
      title: 'Lash Lab',
      validationSchema: yup.object().shape({
        done_lash_before: yup.boolean().required(),
        have_experienced: yup.string().required(),
        have_undergone: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'done_lash_before',
          title: 'Have you ever had lash extensions done before?',
          type: 'toggle',
        },
        {
          ref: 'have_experienced',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the lash extension service.',
          type: 'choices',
          choices: [
            'Unusual sensitivity or skin reactions to components in our lash glue',
            'Moderate to severe allergies',
            'Abnormal eye discharge',
            'No',
          ],
        },
        {
          ref: 'have_undergone',
          title:
            'Have you undergone any of the following procedures in the last 6 months? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the lash extension service.',
          type: 'choices',
          choices: [
            'Eye surgery (including Lasik)',
            'Laser treatment on the face near the eye area',
            'No',
          ],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'lash_lift',
      title: 'Lash Lift',
      validationSchema: yup.object().shape({
        done_lash_before: yup.boolean().required(),
        have_experienced: yup.string().required(),
        have_undergone: yup.string().required(),
        curl_degree: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'done_lash_before',
          title: 'Have you ever had a lash lift done before?',
          type: 'toggle',
        },
        {
          ref: 'have_experienced',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the lash extension service.',
          type: 'choices',
          choices: [
            'Unusual sensitivity or skin reactions to components in our lash solutions',
            'Moderate to severe allergies',
            'Abnormal eye discharge',
            'No',
          ],
        },
        {
          ref: 'have_undergone',
          title:
            'Have you undergone any of the following procedures in the last 6 months? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the lash extension service.',
          type: 'choices',
          choices: [
            'Eye surgery (including Lasik)',
            'Laser treatment on the face near the eye area',
            'No',
          ],
        },
        {
          ref: 'curl_degree',
          title: 'Degree of Curl',
          type: 'choices',
          choices: ['Natural, Subtle Look', 'Dramatic, Bright-Eyed Look'],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'facials',
      title: 'Facials',
      validationSchema: yup.object().shape({
        skin_type: yup.string().required(),
        skin_care_products: stringArraySchema,
        have_undergone: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'skin_type',
          title: 'Skin Type',
          type: 'choices',
          choices: [
            'Normal (no visible blemishes, fine pores, smooth texture)',
            'Combination (oily and dry patches, oily t-zone, hormonal breakouts)',
            'Sensitive (reactive to fragrances, often irritated)',
            'Oily (enlarged pores, excessive oil)',
            'Acne (cystic or nodules)',
            'Dry (dull, visible lines and wrinkles, feels tight)',
          ],
        },
        {
          ref: 'skin_care_products',
          title: 'What skin care products do you use on a daily basis?',
          type: 'choices',
          multiple: true,
          choices: [
            'Soap',
            'Toner',
            'Mask',
            'Eye Cream',
            'SPF',
            'Cleanser',
            'Serum',
            'Exfoliant (physical or chemical)',
            'Moisturizer',
            'Vitamin A (retinol)',
          ],
        },
        {
          ref: 'have_undergone',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the facial service.',
          type: 'choices',
          choices: [
            'Botox, fillers, laser treatment on the face in the last month',
            'Facial surgery in the last 6 months',
            'Cancer in the treatment area ',
            'Skin conditions in the area being treated (e.g. eczema, psoriasis, cold sores, broken skin, sunburn, rosacea)',
            'No',
          ],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'brow_bar',
      title: 'Brow Bar',
      validationSchema: yup.object().shape({
        have_had: yup.boolean().required(),
        have_experienced: yup.string().required(),
        skin_type: yup.string().required(),
        have_undergone: yup.string().required(),
        are_currently: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'have_had',
          title: 'Have you ever had eyebrow embroidery done before?',
          type: 'toggle',
        },
        {
          ref: 'have_experienced',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the eyebrow embroidery service.',
          type: 'choices',
          choices: [
            'Unusual sensitivity or skin reactions',
            'Excessive swelling at the treatment area',
            'Infections at the treatment area',
            'No',
          ],
        },
        {
          ref: 'skin_type',
          title: 'Skin Type',
          type: 'choices',
          choices: [
            'Normal (no visible blemishes, fine pores, smooth texture)',
            'Combination (oily and dry patches, oily t-zone, hormonal breakouts)',
            'Sensitive (reactive to fragrances, often irritated)',
            'Oily (enlarged pores, excessive oil)',
            'Acne (cystic or nodules)',
            'Dry (dull, visible lines and wrinkles, feels tight)',
          ],
        },
        {
          ref: 'have_undergone',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the eyebrow embroidery service.',
          type: 'choices',
          choices: [
            'Botox, fillers, laser treatment on the face in the last month',
            'Facial surgery in the last 6 months',
            'Cancer in the treatment area',
            'Skin conditions in the area being treated (e.g. eczema, psoriasis, cold sores, broken skin, sunburn, rosacea)',
            'No',
          ],
        },
        {
          ref: 'are_currently',
          title: 'Are you currently any of the following?',
          type: 'choices',
          choices: ['Pregnant', 'Breastfeeding', 'On your period', 'NA'],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'brow_henna',
      title: 'Brow Henna',
      validationSchema: yup.object().shape({
        have_had: yup.boolean().required(),
        have_experienced: yup.string().required(),
        have_undergone: yup.string().required(),
        shape_preference: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'have_had',
          title: 'Have you ever had your brows done before?',
          type: 'toggle',
        },
        {
          ref: 'have_experienced',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the brow service.',
          type: 'choices',
          choices: [
            'Unusual sensitivity or skin reactions to components in solutions',
            'Moderate to severe allergies',
            'No',
          ],
        },
        {
          ref: 'have_undergone',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the brow service.',
          type: 'choices',
          choices: [
            'Botox, fillers, laser treatment on the face in the last month',
            'Facial surgery in the last 6 months',
            'Cancer in the treatment area ',
            'Skin conditions in the area being treated (e.g. eczema, psoriasis, cold sores, broken skin, sunburn, rosacea)',
            'No',
          ],
        },
        {
          ref: 'shape_preference',
          title: 'Preferred Brow Shape',
          type: 'choices',
          choices: ['Natural, Subtle Look', 'Dramatic, Angular Look'],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'brow_lamination',
      title: 'Brow Lamination',
      validationSchema: yup.object().shape({
        have_had: yup.boolean().required(),
        have_experienced: yup.string().required(),
        have_undergone: yup.string().required(),
        shape_preference: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'have_had',
          title: 'Have you ever had your brows done before?',
          type: 'toggle',
        },
        {
          ref: 'have_experienced',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the brow service.',
          type: 'choices',
          choices: [
            'Unusual sensitivity or skin reactions to components in solutions',
            'Moderate to severe allergies',
            'No',
          ],
        },
        {
          ref: 'have_undergone',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            'Important Note: if you have experienced any of the above, it might mean that you are not suitable for the brow service.',
          type: 'choices',
          choices: [
            'Botox, fillers, laser treatment on the face in the last month',
            'Facial surgery in the last 6 months',
            'Cancer in the treatment area ',
            'Skin conditions in the area being treated (e.g. eczema, psoriasis, cold sores, broken skin, sunburn, rosacea)',
            'No',
          ],
        },
        {
          ref: 'shape_preference',
          title: 'Preferred Brow Shape',
          type: 'choices',
          choices: ['Natural, Subtle Look', 'Dramatic, Angular Look'],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
    {
      id: 'hair_makeup',
      title: 'Hair & Makeup',
      validationSchema: yup.object().shape({
        preferred_makeup: yup.string().required(),
        skin_type: yup.string().required(),
        have_undergone: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'preferred_makeup',
          title: 'Preferred Makeup Style',
          type: 'choices',
          choices: [
            'Natural and Subtle',
            'Bold and Dramatic',
            'Elegant and Glam',
          ],
        },
        {
          ref: 'skin_type',
          title: 'Skin Type',
          type: 'choices',
          choices: [
            'Normal (no visible blemishes, fine pores, smooth texture)',
            'Combination (oily and dry patches, oily t-zone, hormonal breakouts)',
            'Sensitive (reactive to fragrances, often irritated)',
            'Oily (enlarged pores, excessive oil)',
            'Acne (cystic or nodules)',
            'Dry (dull, visible lines and wrinkles, feels tight)',
          ],
        },
        {
          ref: 'have_undergone',
          title:
            'Have you experienced or undergone any of the following? Please select all that apply.',
          description:
            ' Important Note:  if you have experienced any of the above, it might mean that you are not suitable for the hair & makeup service.',
          type: 'choices',
          choices: [
            'Botox, fillers, laser treatment on the face in the last month',
            'Facial surgery in the last 6 months',
            'Cancer in the treatment area ',
            'Skin conditions in the area being treated (e.g. eczema, psoriasis, cold sores, broken skin, sunburn, rosacea)',
            'No',
          ],
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
        // TODO: Upload Photo
      ],
    },
    {
      id: 'princess_party',
      title: 'Princess Party',
      validationSchema: yup.object().shape({
        average_child_age: yup.string().required(),
        kids_count: yup.string().required(),
        concern: yup.string().required(),
        acceptance: acceptanceSchema,
      }),
      fields: [
        {
          ref: 'average_child_age',
          title: 'What is the average age of the little ones?',
          type: 'choices',
          choices: [
            '3-5 years old',
            '6-8 years old',
            '9-11 years old',
            '12-15 years old',
            '>15 years old',
          ],
        },
        {
          ref: 'kids_count',
          title: 'How many kids will you be expecting?',
          type: 'text',
        },
        {
          ref: 'concern',
          title: 'Concerns',
          description:
            'Please let us know if there are any particular concerns you would like us to take note of.',
          type: 'text',
        },
        {
          ref: 'acceptance',
          children:
            '<div>I declare that the above is true and accurate, and accept <a href="/terms-of-service" target="_blank" class="underline">the terms and conditions in relation to the provision of my beauty service.</a></div>',
          type: 'checkbox',
        },
      ],
    },
  ],
};
export default Constants;
